/**
 * TestingNextRunSummary — what the NEXT rotation does to this whole segment, in
 * one line: how many elements go back to the queue, how many are eliminated,
 * how many are held back and how many are left alone. The per-row column says
 * what happens to each element; this says how big the run is before you read a
 * single row.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { AlertTriangle, Clock, Hourglass, Minus, Trash2 } from 'lucide-react';
import type { QueueElement, RotationElementDiff } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

import { poolElementKey, type RotationSwap } from '../../rotation/rotation-swaps';

export interface NextRunTotals {
  requeue: number;
  /** Capacity evictions — parked in Waiting, not back in the line. */
  toWaiting: number;
  eliminate: number;
  held: number;
  untouched: number;
  /** Of the leaving elements, how many have a named replacement queued. */
  replaced: number;
}

export function nextRunTotals(
  elements: QueueElement[],
  atRisk: Map<string, RotationSwap>,
  blocked: Map<string, RotationElementDiff>,
): NextRunTotals {
  const totals: NextRunTotals = {
    requeue: 0,
    toWaiting: 0,
    eliminate: 0,
    held: 0,
    untouched: 0,
    replaced: 0,
  };
  for (const el of elements) {
    const swap = atRisk.get(poolElementKey(el));
    if (swap?.out) {
      if (swap.out.action === 'eliminate') totals.eliminate += 1;
      else if (swap.out.toWaiting) totals.toWaiting += 1;
      else totals.requeue += 1;
      if (swap.in) totals.replaced += 1;
    } else if (blocked.has(poolElementKey(el))) totals.held += 1;
    else totals.untouched += 1;
  }
  return totals;
}

const ITEMS = [
  { key: 'requeue', label: 'back to queue', icon: Clock, tone: 'text-primary' },
  { key: 'toWaiting', label: 'to waiting', icon: Hourglass, tone: 'text-primary' },
  { key: 'eliminate', label: 'eliminated', icon: Trash2, tone: 'text-destructive' },
  { key: 'held', label: 'held', icon: AlertTriangle, tone: 'text-warning' },
  { key: 'untouched', label: 'unchanged', icon: Minus, tone: 'text-muted-foreground' },
] as const;

const TestingNextRunSummary: FC<{ totals: NextRunTotals }> = ({ totals }) => {
  const changes = totals.requeue + totals.toWaiting + totals.eliminate;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-2 text-[11px]">
      <span className="font-semibold uppercase tracking-wide text-muted-foreground">
        Next run
      </span>
      <span className={cn('font-semibold tabular-nums', changes ? 'text-foreground' : 'text-muted-foreground')}>
        {changes === 0 ? 'no changes' : `${changes} change${changes === 1 ? '' : 's'}`}
      </span>
      {changes > 0 && totals.replaced > 0 ? (
        <span className="tabular-nums text-muted-foreground">
          {totals.replaced} replaced
        </span>
      ) : null}
      <span className="text-border" aria-hidden>
        |
      </span>
      {ITEMS.map(({ key, label, icon: Icon, tone }) =>
        totals[key] > 0 ? (
          <span key={key} className={cn('inline-flex items-center gap-1 tabular-nums', tone)}>
            <Icon className="h-3 w-3 shrink-0" aria-hidden />
            <span className="font-semibold">{totals[key]}</span>
            <span className="text-muted-foreground">{label}</span>
          </span>
        ) : null,
      )}
    </div>
  );
};

export default TestingNextRunSummary;
